import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { errResult } from 'libs/end-to-end/src/result';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { CustomVizDefinition } from 'libs/end-to-end/src/custom-viz-definition';
import { CustomVizOptionsSummary } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useRef } from 'react';
import { useState } from 'react';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';

export const useCustomVizAutoloadDropdownsEffect = ({
  customVizDefinition,
  observation,
  organizationId,
  setIsLoadingDropdownOptions,
  userAuthenticatedApiCall,
}: {
  customVizDefinition: CustomVizDefinition;
  observation: ObservationLocationType;
  organizationId: string;
  setIsLoadingDropdownOptions: Setter<boolean>;
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
}) => {
  const [loadDropdownOptionsResult, setLoadDropdownOptionsResult] = useState<
    Result<CustomVizOptionsSummary | null, BaseError>
  >(okResult(null));

  const loadedDropdownOptionsDef = useRef<{
    observation: ObservationLocationType;
    customVizId: string;
  } | null>();

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive()) {
        return;
      }

      if (
        loadedDropdownOptionsDef.current?.customVizId ===
          customVizDefinition.customVizId &&
        observation.databaseRevisionId ===
          loadedDropdownOptionsDef.current?.observation?.databaseRevisionId
      ) {
        return;
      }

      loadedDropdownOptionsDef.current = {
        customVizId: customVizDefinition.customVizId,
        observation,
      };

      try {
        setIsLoadingDropdownOptions(true);
        setLoadDropdownOptionsResult(okResult(null));

        let queryPart: string;
        if (observation.type === 'db-revision') {
          queryPart = `databaseId=${observation.databaseId}&databaseRevisionId=${observation.databaseRevisionId}`;
        } else {
          throw guardExhaustive(observation.type);
        }

        const getOptionsResult = await userAuthenticatedApiCall({
          responseSchema: CustomVizOptionsSummary,
          url: `${getApiBaseUrl()}/v2/custom-viz/options?${queryPart}&customVizId=${customVizDefinition.customVizId}&organizationId=${organizationId}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (!isAlive() || !getOptionsResult) {
          return;
        }

        if (getOptionsResult.resultType === 'err') {
          setLoadDropdownOptionsResult(errResult(getOptionsResult.error));
          return;
        }

        setLoadDropdownOptionsResult(okResult(getOptionsResult.value));
      } finally {
        if (isAlive()) {
          setIsLoadingDropdownOptions(false);
        }
      }
    },
    [
      customVizDefinition,
      setIsLoadingDropdownOptions,
      observation,
      userAuthenticatedApiCall,
      organizationId,
    ],
  );

  return loadDropdownOptionsResult;
};
